import { useState, useEffect, useRef, lazy, Suspense, memo } from 'react';
import { Link } from 'react-router-dom';
import {
  FiTruck, FiRotateCcw, FiShield, FiFeather, FiArrowRight, FiChevronLeft, FiChevronRight, FiPlus, FiMinus, FiGift, FiBriefcase,
} from 'react-icons/fi';
import { FaStar } from 'react-icons/fa';
import HeroSlider from '../components/HeroSlider';
import ShopByAge from '../components/ShopByAge';
import ProductCard from '../components/ProductCard';
import API from '../utils/api';
import { setStructuredData, generateCollectionSchema, generateFAQSchema, DEFAULT_FAQ, setSEO, PAGE_SEO } from '../utils/seo';
import { getCategoryImage, handleImageError, responsiveImage } from '../utils/imageHelper';
import { AGE_GROUPS, ageLink } from '../data/ageGroups';
import GUIDES from '../data/guides.json';

// Below the fold and only shown when reels exist — fetched on demand
const ProductReels = lazy(() => import('../components/ProductReels'));

const TRUST = [
  { icon: FiTruck,     text: 'Free shipping above ₹499' },
  { icon: FiFeather,   text: 'Child-safe, non-toxic' },
  { icon: FiRotateCcw, text: '7-day easy returns' },
  { icon: FiShield,    text: 'Secure UPI, cards & COD' },
];

const NO_IMG = 'default-category.jpg';
const NONE = []; // stable empty list so memoised rails don't re-render while loading

/* ── Building blocks ──────────────────────────────────────────── */
const SectionHeader = ({ title, subtitle, link, linkLabel = 'View all', children, as: Tag = 'h2' }) => (
  <div className="flex items-end justify-between gap-4 mb-4 md:mb-5">
    <div className="min-w-0">
      <Tag className="font-serif text-lg md:text-2xl text-espresso">{title}</Tag>
      {subtitle && <p className="hidden sm:block font-sans text-sm text-wood-400 mt-0.5">{subtitle}</p>}
    </div>
    <div className="flex items-center gap-3 flex-shrink-0">
      {children}
      {link && (
        <Link to={link} className="inline-flex items-center gap-1 font-sans font-semibold text-[13px] text-espresso hover:text-terra transition-colors">
          {linkLabel} <FiArrowRight className="text-xs" />
        </Link>
      )}
    </div>
  </div>
);

const SkeletonCard = () => (
  <div className="animate-pulse rounded-2xl border border-skin-100 overflow-hidden bg-white">
    <div className="aspect-square bg-skin-100" />
    <div className="p-3 space-y-2">
      <div className="h-3 rounded-full w-3/4 bg-skin-100" />
      <div className="h-3 rounded-full w-1/2 bg-skin-100" />
    </div>
  </div>
);

/* Horizontal product rail with arrow controls (memo: only re-renders when its own list arrives) */
const ProductRail = memo(({ title, subtitle, link, products, loading, tint = false }) => {
  const ref = useRef(null);
  const scroll = dir => ref.current?.scrollBy({ left: dir * ref.current.clientWidth * 0.9, behavior: 'smooth' });
  if (!loading && products.length === 0) return null;

  return (
    <section className={`py-8 md:py-12 ${tint ? 'bg-skin-50' : 'bg-white'}`}>
      <div className="container-custom">
        <SectionHeader title={title} subtitle={subtitle} link={link}>
          <div className="hidden md:flex gap-1.5">
            <button type="button" onClick={() => scroll(-1)} aria-label={`Scroll ${title} left`}
              className="w-8 h-8 rounded-full border border-skin-200 bg-white text-espresso flex items-center justify-center hover:bg-espresso hover:text-white hover:border-espresso transition">
              <FiChevronLeft />
            </button>
            <button type="button" onClick={() => scroll(1)} aria-label={`Scroll ${title} right`}
              className="w-8 h-8 rounded-full border border-skin-200 bg-white text-espresso flex items-center justify-center hover:bg-espresso hover:text-white hover:border-espresso transition">
              <FiChevronRight />
            </button>
          </div>
        </SectionHeader>
        <div ref={ref} className="flex gap-3 md:gap-4 overflow-x-auto scrollbar-hide snap-x scroll-px-4 sm:scroll-px-0 -mx-4 px-4 sm:mx-0 sm:px-0 pb-1">
          {(loading ? [...Array(5)] : products).map((p, i) => (
            <div key={p?._id || i} className="snap-start flex-shrink-0 w-[46%] sm:w-[31%] md:w-[23.5%] lg:w-[calc(20%-13px)]">
              {loading ? <SkeletonCard /> : <ProductCard product={p} />}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
});

/* Real store-wide rating + recent customer reviews. Renders nothing until reviews exist. */
const ParentsLove = ({ summary }) => {
  if (!(summary?.totalReviews > 0) || typeof summary.averageRating !== 'number') return null;
  const { averageRating, totalReviews, totalSold, testimonials = [] } = summary;
  return (
    <section className="py-8 md:py-12 bg-white" aria-labelledby="reviews-heading">
      <div className="container-custom">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-5">
          <div>
            <h2 id="reviews-heading" className="font-serif text-lg md:text-2xl text-espresso">Loved by parents</h2>
            <p className="font-sans text-sm text-wood-400 mt-0.5">Real reviews from Thrayam Toys customers</p>
          </div>
          <div className="flex items-center gap-4 font-sans">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-espresso">{averageRating.toFixed(1)}</span>
              <span>
                <span className="flex gap-0.5" aria-label={`${averageRating.toFixed(1)} out of 5 stars`}>
                  {[...Array(5)].map((_, i) => (
                    <FaStar key={i} className={`text-sm ${i < Math.round(averageRating) ? 'text-gold-400' : 'text-skin-200'}`} />
                  ))}
                </span>
                <span className="block text-[11px] text-wood-400">{totalReviews.toLocaleString('en-IN')} reviews</span>
              </span>
            </div>
            {totalSold >= 100 && (
              <div className="pl-4 border-l border-skin-200">
                <span className="block text-2xl font-bold text-espresso">{totalSold.toLocaleString('en-IN')}+</span>
                <span className="block text-[11px] text-wood-400">toys delivered</span>
              </div>
            )}
          </div>
        </div>
        {testimonials.length > 0 && (
          <ul className="flex md:grid md:grid-cols-3 gap-3 md:gap-4 overflow-x-auto scrollbar-hide snap-x -mx-4 px-4 sm:mx-0 sm:px-0">
            {testimonials.slice(0, 3).map(t => (
              <li key={t._id} className="snap-start flex-shrink-0 w-[82%] sm:w-[48%] md:w-auto rounded-2xl border border-skin-100 bg-skin-50 p-4 md:p-5 flex flex-col">
                <span className="flex gap-0.5 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <FaStar key={i} className={`text-xs ${i < t.rating ? 'text-gold-400' : 'text-skin-200'}`} />
                  ))}
                </span>
                <blockquote className="font-sans text-sm text-espresso leading-relaxed line-clamp-5 flex-1">“{t.comment}”</blockquote>
                <p className="font-sans text-xs text-wood-400 mt-3">
                  <span className="font-semibold text-espresso">{t.name}</span>
                  {t.product?.slug && <> on <Link to={`/products/${t.product.slug}`} className="underline underline-offset-2 hover:text-terra">{t.product.name}</Link></>}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

const FaqItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-skin-100">
      <h3 className="m-0">
        <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}
          className="w-full flex items-center justify-between gap-4 py-3.5 text-left font-sans font-semibold text-sm text-espresso">
          {question}
          {open ? <FiMinus className="flex-shrink-0 text-terra" /> : <FiPlus className="flex-shrink-0 text-wood-400" />}
        </button>
      </h3>
      {/* Answer stays in the DOM so crawlers can read it */}
      <div className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-96 pb-4' : 'max-h-0'}`}>
        <p className="font-sans text-sm text-wood-400 leading-relaxed">{answer}</p>
      </div>
    </div>
  );
};

/* ── Page ─────────────────────────────────────────────────────── */
const Home = () => {
  const [categories, setCategories] = useState([]);
  const [lists, setLists] = useState({ popular: null, newest: null, featured: null });
  const [reels, setReels] = useState([]);
  const [reelsEnabled, setReelsEnabled] = useState(true);
  const [email, setEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('idle');
  const [reviewSummary, setReviewSummary] = useState(null);

  useEffect(() => {
    setSEO(PAGE_SEO.home);
    setStructuredData(generateFAQSchema(DEFAULT_FAQ), 'homepage-faq-data');

    API.get('/categories').then(({ data }) => setCategories(data.categories || [])).catch(console.error);
    API.get('/products/review-summary').then(({ data }) => setReviewSummary(data)).catch(() => {});

    const load = (key, url) => API.get(url)
      .then(({ data }) => data.products || [])
      .catch(() => [])
      .then(items => {
        setLists(l => ({ ...l, [key]: items }));
        if (key === 'popular' && items.length) {
          setStructuredData(
            generateCollectionSchema({ name: 'Top Selling Toys', description: 'Bestselling wooden and educational toys at Thrayam Toys' }, items),
            'homepage-collection-data'
          );
        }
      });
    load('popular',  '/products?sort=popular&limit=10');
    load('newest',   '/products?sort=newest&limit=10');
    load('featured', '/products?featured=true&limit=10');

    Promise.all([
      API.get('/reels/active').catch(() => ({ data: { reels: [] } })),
      API.get('/settings/public').catch(() => ({ data: {} })),
    ]).then(([reelsRes, settingsRes]) => {
      setReels(reelsRes.data.reels || []);
      const v = settingsRes.data.settings?.reels_enabled;
      setReelsEnabled(v === undefined ? true : v);
    });
  }, []);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!email) return;
    setNewsletterStatus('loading');
    try {
      await API.post('/newsletter/subscribe', { email, source: 'home' });
      setNewsletterStatus('success');
      setEmail('');
    } catch {
      setNewsletterStatus('error');
    }
  };

  return (
    <div className="bg-white">

      {/* ── Hero banner ── */}
      <section className="container-custom pt-3 md:pt-5">
        <HeroSlider className="h-[190px] sm:h-[280px] lg:h-[380px]" />
      </section>

      {/* ── Trust strip ── */}
      <section aria-label="Why shop with us" className="container-custom">
        <ul className="flex md:grid md:grid-cols-4 gap-x-6 gap-y-1 overflow-x-auto scrollbar-hide py-3 md:py-4 border-b border-skin-100">
          {TRUST.map(({ icon: Icon, text }) => (
            <li key={text} className="flex-shrink-0 flex items-center md:justify-center gap-2 font-sans text-[12px] md:text-[13px] font-medium text-espresso">
              <Icon className="text-sage-500 text-sm" /> {text}
            </li>
          ))}
        </ul>
      </section>

      {/* ── Shop by age ── */}
      <ShopByAge as="h1" title={PAGE_SEO.home.h1} />

      {/* ── Top selling ── */}
      <ProductRail tint title="Top Selling" subtitle="Our most-loved toys right now"
        link="/products?sort=popular" products={lists.popular || NONE} loading={!lists.popular} />

      {/* ── Social proof (real reviews only) ── */}
      <ParentsLove summary={reviewSummary} />

      {/* ── Shop by category ── */}
      {categories.length > 0 && (
        <section className="py-8 md:py-12 bg-white">
          <div className="container-custom">
            <SectionHeader title="Shop by Category" link="/products" linkLabel="All toys" />
            <ul className="flex lg:grid lg:grid-cols-8 gap-3 md:gap-4 overflow-x-auto lg:overflow-visible scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
              {categories.slice(0, 8).map(cat => {
                const hasImg = cat.image && cat.image !== NO_IMG;
                return (
                  <li key={cat._id} className="flex-shrink-0 w-[22%] sm:w-[16%] lg:w-auto">
                    <Link to={`/products?category=${cat._id}`} className="group flex flex-col items-center gap-2 text-center">
                      <span className="w-full aspect-square rounded-2xl overflow-hidden bg-skin-50 border border-skin-100 flex items-center justify-center">
                        {hasImg
                          ? <img src={getCategoryImage(cat)} alt={`${cat.name} toys`} loading="lazy" onError={e => handleImageError(e, 'category')}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                          : <span className="font-sans font-bold text-xl text-wood-400">{cat.name?.charAt(0)}</span>}
                      </span>
                      <span className="font-sans text-xs md:text-[13px] font-semibold text-espresso leading-tight line-clamp-2 group-hover:text-terra">{cat.name}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      )}

      {/* ── New arrivals grid ── */}
      {(lists.newest === null || lists.newest.length > 0) && (
        <section className="py-8 md:py-12 bg-skin-50">
          <div className="container-custom">
            <SectionHeader title="New Arrivals" subtitle="Fresh additions to the toy box" link="/products?sort=newest" />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
              {(lists.newest || [...Array(10)]).map((p, i) => p ? <ProductCard key={p._id} product={p} /> : <SkeletonCard key={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* ── Promo banners ── */}
      <section className="py-8 md:py-12 bg-white">
        <div className="container-custom grid md:grid-cols-2 gap-3 md:gap-4">
          {[
            { to: ageLink(AGE_GROUPS[2]), eyebrow: 'Montessori essentials', title: 'Independent play for little hands', cta: 'Shop 1–2 years', bg: 'bg-sage-100', tone: 'text-sage-600',
              img: 'https://images.unsplash.com/photo-1617331140180-e8262094733a', alt: 'Montessori wooden toys for toddlers' },
            { to: '/gift-guide', eyebrow: 'Gift guide', title: 'Birthday, baby shower & return gifts', cta: 'Find a gift', bg: 'bg-gold-100', tone: 'text-gold-600',
              img: 'https://images.unsplash.com/photo-1545558014-8692077e9b5c', alt: 'Wooden toy gifts for kids' },
          ].map(b => (
            <Link key={b.title} to={b.to} className={`group relative rounded-2xl overflow-hidden ${b.bg} min-h-[170px] md:min-h-[220px] flex items-center`}>
              <div className="relative z-10 p-5 md:p-8 max-w-[58%]">
                <p className={`font-sans text-[10px] md:text-[11px] font-semibold tracking-[0.16em] uppercase ${b.tone} mb-1.5`}>{b.eyebrow}</p>
                <p className="font-serif text-lg md:text-2xl text-espresso mb-3 leading-tight">{b.title}</p>
                <span className="inline-flex items-center gap-1.5 font-sans font-semibold text-[13px] text-espresso border-b-2 border-espresso pb-0.5 group-hover:gap-2.5 transition-all">
                  {b.cta} <FiArrowRight className="text-xs" />
                </span>
              </div>
              <img {...responsiveImage(b.img, [320, 480, 640])} sizes="(min-width: 768px) 270px, 42vw" alt={b.alt} loading="lazy"
                width="640" height="480"
                className="absolute right-0 top-0 h-full w-[42%] object-cover transition-transform duration-500 group-hover:scale-105" />
            </Link>
          ))}
        </div>
      </section>

      {/* ── Staff picks ── */}
      <ProductRail tint title="Staff Picks" subtitle="Handpicked by our team"
        link="/products?featured=true" products={lists.featured || NONE} loading={!lists.featured} />

      {/* ── Reels ── */}
      {reelsEnabled && reels.length > 0 && (
        <Suspense fallback={null}><div className="bg-white"><ProductReels reels={reels} /></div></Suspense>
      )}

      {/* ── Gift cards + bulk orders ── */}
      <section className="py-6 md:py-8 bg-white">
        <div className="container-custom grid sm:grid-cols-2 gap-3 md:gap-4">
          <Link to="/gift-cards" className="group flex items-center gap-4 rounded-2xl border border-skin-100 bg-skin-50 p-4 md:p-5 hover:border-skin-200 transition">
            <span className="w-11 h-11 rounded-full bg-terra-100 text-terra flex items-center justify-center flex-shrink-0"><FiGift className="text-lg" /></span>
            <span className="min-w-0">
              <span className="block font-sans font-bold text-sm text-espresso">Not sure what they have? Send a gift card</span>
              <span className="block font-sans text-xs text-wood-400">₹500–₹10,000 · delivered instantly on WhatsApp or email</span>
            </span>
            <FiArrowRight className="ml-auto text-espresso flex-shrink-0 group-hover:translate-x-1 transition" />
          </Link>
          <Link to="/bulk-orders" className="group flex items-center gap-4 rounded-2xl border border-skin-100 bg-skin-50 p-4 md:p-5 hover:border-skin-200 transition">
            <span className="w-11 h-11 rounded-full bg-sage-100 text-sage-600 flex items-center justify-center flex-shrink-0"><FiBriefcase className="text-lg" /></span>
            <span className="min-w-0">
              <span className="block font-sans font-bold text-sm text-espresso">Preschools, return gifts & corporate orders</span>
              <span className="block font-sans text-xs text-wood-400">Volume pricing, gift packing and GST invoices</span>
            </span>
            <FiArrowRight className="ml-auto text-espresso flex-shrink-0 group-hover:translate-x-1 transition" />
          </Link>
        </div>
      </section>

      {/* ── Toy guides ── */}
      <section className="py-8 md:py-12 bg-skin-50 border-t border-skin-100">
        <div className="container-custom">
          <SectionHeader title="Toy guides by age" subtitle="What your child is learning now, and the toys that help" link="/blogs" linkLabel="All guides" />
          <ul className="flex md:grid md:grid-cols-4 gap-3 md:gap-4 overflow-x-auto scrollbar-hide snap-x -mx-4 px-4 sm:mx-0 sm:px-0">
            {GUIDES.filter(g => g.tag === 'Milestones').slice(0, 4).map(g => (
              <li key={g.slug} className="snap-start flex-shrink-0 w-[70%] sm:w-[45%] md:w-auto">
                <Link to={`/blogs/${g.slug}`} className="group block h-full rounded-2xl bg-white border border-skin-100 p-4 hover:border-skin-200 transition">
                  <span className="font-sans text-[10px] font-semibold uppercase tracking-wider text-sage-500">{g.read} read</span>
                  <h3 className="font-sans font-bold text-sm text-espresso leading-snug mt-1 group-hover:text-terra">{g.title}</h3>
                  <p className="font-sans text-xs text-wood-400 mt-1.5 line-clamp-3">{g.summary}</p>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── About + FAQ (visible text backs the FAQPage structured data) ── */}
      <section className="py-10 md:py-14 bg-white border-t border-skin-100" aria-labelledby="about-heading">
        <div className="container-custom grid lg:grid-cols-5 gap-8 lg:gap-14">
          <div className="lg:col-span-2">
            <h2 id="about-heading" className="font-serif text-lg md:text-xl text-espresso mb-3">
              Wooden &amp; educational toys online in India
            </h2>
            <div className="font-sans text-[13px] text-wood-400 leading-relaxed space-y-3">
              <p>
                Thrayam Toys curates wooden toys, Montessori-inspired learning toys, puzzles and pretend-play
                sets that are safe, durable and genuinely useful at each stage of a child's development.
              </p>
              <p>
                Shop by age — from <Link to={ageLink(AGE_GROUPS[0])} className="text-espresso underline underline-offset-2">newborn toys</Link>,{' '}
                <Link to={ageLink(AGE_GROUPS[1])} className="text-espresso underline underline-offset-2">toys for 6–12 month babies</Link> and{' '}
                <Link to={ageLink(AGE_GROUPS[2])} className="text-espresso underline underline-offset-2">toys for 1 year olds</Link> to{' '}
                <Link to={ageLink(AGE_GROUPS[3])} className="text-espresso underline underline-offset-2">2 year olds</Link> and{' '}
                <Link to={ageLink(AGE_GROUPS[4])} className="text-espresso underline underline-offset-2">preschoolers</Link>.
                Free shipping above ₹499 across India, with UPI, card and COD payments.
              </p>
            </div>
          </div>
          <div className="lg:col-span-3">
            <h2 className="font-serif text-lg md:text-xl text-espresso mb-1">Frequently asked questions</h2>
            {DEFAULT_FAQ.map(f => <FaqItem key={f.question} {...f} />)}
          </div>
        </div>
      </section>

      {/* ── Newsletter ── */}
      <section className="py-8 md:py-10 bg-skin-50 border-t border-skin-100">
        <div className="container-custom flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-center md:text-left">
            <p className="font-serif text-lg md:text-xl text-espresso">Get toy tips &amp; early access to offers</p>
            <p className="font-sans text-[13px] text-wood-400">Age-by-age play guides and members-only deals. No spam.</p>
          </div>
          {newsletterStatus === 'success' ? (
            <p className="font-sans font-semibold text-sm text-sage-600">Thanks for subscribing!</p>
          ) : (
            <form onSubmit={handleNewsletter} className="w-full md:w-auto md:min-w-[400px]">
              <div className="flex gap-2">
                <label htmlFor="newsletter-email" className="sr-only">Email address</label>
                <input id="newsletter-email" type="email" required value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="Your email address"
                  className="flex-1 min-w-0 h-11 px-4 rounded-full font-sans text-sm bg-white border border-skin-200 text-espresso focus:outline-none focus:border-espresso" />
                <button type="submit" disabled={newsletterStatus === 'loading'}
                  className="h-11 px-6 rounded-full font-sans font-semibold text-sm bg-espresso text-white hover:bg-wood-600 transition disabled:opacity-60">
                  {newsletterStatus === 'loading' ? '…' : 'Subscribe'}
                </button>
              </div>
              {newsletterStatus === 'error' && (
                <p className="font-sans text-xs text-terra mt-2">Couldn't subscribe right now — please try again later.</p>
              )}
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default Home;
